<script lang="ts">
	// @ts-nocheck

	import Button from '$lib/components/ui/button/button.svelte';
	import { ArrowRight, CheckCircle, Loader2 } from 'lucide-svelte';
	import { Input } from '$lib/components/ui/input';
	import { Label } from '$lib/components/ui/label';
	import * as Select from '$lib/components/ui/select';
	import { Textarea } from '$lib/components/ui/textarea';
	import * as Alert from '$lib/components/ui/alert';
	import { fly } from 'svelte/transition';
	import { onMount } from 'svelte';
	import Seo from '$lib/components/SEO.svelte';
	import { trackPhoneClick } from '$lib/utils/analytics';
	import tallTreesImg from '$lib/assets/curated/tall-trees.png?enhanced';
	import historyWalkImg from '$lib/assets/curated/history-walk.png?enhanced';
	import starGazingImg from '$lib/assets/curated/star-gazing.png?enhanced';
	import fieldExpertsImg from '$lib/assets/curated/field-experts.jpg?enhanced';
	import airExperiencesImg from '$lib/assets/curated/air-experiences.jpg?enhanced';
	import hikingImg from '$lib/assets/curated/hiking.jpg?enhanced';
	import meetMakersImg from '$lib/assets/curated/meet-the-makers.jpg?enhanced';
	import curatedStaysImg from '$lib/assets/curated/curated-stays.jpg?enhanced';

	onMount(() => {
		setTimeout(() => {
			window.scrollTo(0, 0);
		}, 200);
	});

	const contactMethods = [
		{ value: 'phone', label: 'Phone' },
		{ value: 'email', label: 'Email' }
	];

	let submitted = $state(false);
	let loading = $state(false);

	let name = $state('');
	let email = $state('');
	let phone = $state('');
	let preferredContact = $state('Either');
	let message = $state('');
	let type = 'Bespoke Tasmania Experiences Form';

	const triggerContent = $derived(
		contactMethods.find((f) => f.value === preferredContact)?.label ?? 'Either'
	);

	function resetForm() {
		name = '';
		email = '';
		phone = '';
		preferredContact = '';
		message = '';
	}

	async function handleSubmit(e: Event) {
		e.preventDefault();
		loading = true;
		const formData = {
			name,
			email,
			phone,
			preferredContact,
			message,
			type
		};

		try {
			const response = await fetch('/email/send-email', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(formData)
			});

			if (!response.ok) {
				throw new Error('Failed to send email');
			}

			const result = await response.json();

			if (result.success) {
				resetForm();
				submitted = true;
			} else {
				throw new Error(result.error || 'Failed to send email');
			}
		} catch (error) {
			console.error('Error sending email:', error);
		} finally {
			loading = false;
		}
	}

	function handleAnchorClick(event) {
		event.preventDefault();
		const link = event.currentTarget;
		const anchorId = new URL(link.href).hash.replace('#', '');
		const anchor = document.getElementById(anchorId);
		window.scrollTo({
			top: anchor.offsetTop,
			behavior: 'smooth'
		});
	}

	const curatedExperiences = [
		{
			image: tallTreesImg,
			title: 'Tall Trees Adventure',
			content:
				"Walk beneath the giants with an ecologist who will share his knowledge about some of the world's most rare and special trees."
		},
		{
			image: historyWalkImg,
			title: 'Guided History Walk',
			content:
				'Discover the history and mystery of the oldest mental asylum in Tasmania with a specialised historian as you walk through the atmospheric landmarks.'
		},
		{
			image: starGazingImg,
			title: 'Star Gazing with an Astronomer',
			content:
				"Explore our magnificent night sky with an astronomer who gives deeper insight into the constellations, planets, meteor showers and the Southern Cross. Catch the aurora borealis if you're lucky!"
		},
		{
			image: fieldExpertsImg,
			title: 'Experience the Passion of Field Experts',
			content:
				'Connecting you with world class, yet local subject matter experts, this unexpected adventure is the perfect opportunity to appease your curiosity and love for nature, for an experience you will never forget.'
		},
		{
			image: airExperiencesImg,
			title: 'Signature Air Experiences',
			content:
				"See Tasmania from an entirely new perspective with exclusive helicopter and scenic flight experiences showcasing the island's most breathtaking landscapes. Whether it's a private helicopter transfer, a scenic coastal flight, or an alpine landing experience, every detail is curated for impact and exclusivity."
		},
		{
			image: hikingImg,
			title: 'Hiking Experiences',
			content:
				"Tasmania's landscapes are best experienced on foot. Our curated hiking experiences take you from rugged coastlines to ancient forests at a relaxed pace, with guided insight, fresh air, and time to truly take in the natural surroundings."
		},
		{
			image: meetMakersImg,
			title: 'Meet The Makers',
			content:
				"An exclusive journey into Tasmania's most celebrated food, wine, and artisan culture. Meet the makers behind the island's finest produce—award-winning winemakers, distillers, growers, and craftspeople through intimate, behind-the-scenes experiences designed for genuine connection and insight."
		},
		{
			image: curatedStaysImg,
			title: 'Curated Stays Tasmania',
			content:
				"A hand-selected collection of Tasmania's most exceptional accommodation, chosen for character, comfort, and sense of place. More than accommodation, these properties are an integral part of the journey offering privacy, refined comfort, and a deeper connection to Tasmania's landscapes, produce, and lifestyle."
		}
	];
</script>

<Seo
	title="Bespoke Tasmania Experiences | Bespoke Luxury Tours - Tassie Tours"
	description="Bespoke, behind-the-scenes Tasmanian experiences crafted for discerning travellers. Private guides, exclusive access, and unforgettable moments. Enquire with Tassie Tours."
	keywords="Bespoke Tasmania tours, luxury Tasmania experiences, curated Tasmania, private guides, exclusive Tasmania, high-end Tasmania tours"
	siteName="Tassie Tours"
	imageURL="/images/hero-carousel-1.jpg"
	logo="/src/lib/assets/logo/logo-black.png"
	author="Tassie Tours"
	type="website"
	name="Tassie Tours"
	index={true}
	twitter={true}
	openGraph={true}
	schemaOrg={true}
/>

<section class="relative mx-auto flex h-[600px] flex-col items-center justify-center">
	<enhanced:img
		src="$lib/assets/curated/curated-tours.jpg"
		alt="Bespoke Tasmania Experiences"
		class="absolute inset-0 h-[600px] w-full object-cover"
	/>

	<div class="absolute inset-0 bg-foreground/60"></div>

	<div
		class="pointer-events-none absolute inset-0 mx-auto flex h-[600px] w-full max-w-7xl flex-col justify-center px-4"
	>
		<p
			class="mx-auto max-w-2xl text-center text-sm font-semibold uppercase tracking-[0.3em] text-background/80 lg:mx-0 lg:text-left"
		>
			Bespoke &amp; Exclusive
		</p>
		<h1
			class="mx-auto mt-4 max-w-3xl scroll-m-20 text-center font-heading text-2xl font-bold tracking-tight text-background lg:mx-0 lg:text-left lg:text-5xl"
		>
			Bespoke Tasmania Experiences
		</h1>
		<div class="mx-auto mt-6 flex max-w-2xl items-center lg:mx-0">
			<p class="text-md mx-auto text-center text-background lg:mx-0 lg:text-left lg:text-lg">
				For the traveller who wants more than a tour. Step behind the scenes of Tasmania with private
				access, world-class specialists, and moments crafted entirely around you. These are our most
				exclusive, in-depth and personal experiences the island has to offer.
			</p>
		</div>
		<div class=" mt-8 flex max-w-lg flex-col-reverse items-center gap-4 lg:flex-row">
			<Button
				href="#contact"
				onclick={handleAnchorClick}
				data-sveltekit-noscroll
				class="pointer-events-auto mx-auto w-48 rounded-full bg-tt-red px-6 text-lg font-semibold text-background hover:bg-tt-red/85 lg:mx-0"
				>Enquire now</Button
			>
			<Button
				href="#experiences"
				onclick={handleAnchorClick}
				variant="outline"
				data-sveltekit-noscroll
				class="pointer-events-auto mx-auto w-48 rounded-full border-2 bg-transparent px-6 text-lg font-semibold text-background lg:mx-0"
				>Explore experiences</Button
			>
		</div>
	</div>
</section>

<section class="mx-auto bg-secondary px-4 py-12 lg:py-20">
	<div class="mx-auto max-w-7xl px-4">
		<h2 class="max-w-3xl font-heading text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
			An in-depth, behind-the-scenes Tasmania
		</h2>
		<div class="mt-6 grid grid-cols-1 gap-x-8 gap-y-6 lg:grid-cols-2">
			<p class="col-span-1 leading-7">
				Our expert guides are deeply connected to the land, bringing a wealth of specialised
				knowledge to every journey. From historians and ecologists to specialists in tailored
				travel, each experience is enriched with insight, context, and genuine local perspective.
			</p>
			<p class="col-span-1 leading-7">
				Whether your interests lie in wildlife, history, photography, or fine food and wine, every
				journey is thoughtfully shaped to suit your preferences. For those seeking something truly
				exceptional, bespoke experiences can be created — no request is too big or too small.
			</p>
		</div>
		<Button
			href="#contact"
			onclick={handleAnchorClick}
			variant="link"
			data-sveltekit-noscroll
			class="mt-4 gap-2 px-0 text-base leading-7"
			>Start your Tasmanian adventure <ArrowRight class="h-4 w-4" /></Button
		>
	</div>
</section>

<section class="mx-auto w-full bg-secondary" id="experiences">
	<div class="mx-auto max-w-7xl px-4 py-16 lg:py-24">
		<div class="mx-auto max-w-3xl text-center">
			<h2
				class="font-heading text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl"
			>
				Signature Curated Experiences
			</h2>
			<p class="mt-4 leading-7 text-muted-foreground">
				A selection of our most exclusive journeys. Each can be enjoyed on its own or woven into a
				larger bespoke itinerary.
			</p>
		</div>

		<div class="mt-12 flex flex-col gap-16 lg:mt-16 lg:gap-24">
			{#each curatedExperiences as experience, index}
				<div class="grid grid-cols-1 items-center gap-x-12 gap-y-8 lg:grid-cols-2">
					<div class={index % 2 === 1 ? 'lg:order-2' : ''}>
						<div class="overflow-hidden rounded-2xl bg-gray-100 shadow-xl">
							<enhanced:img
								src={experience.image}
								alt={experience.title}
								class="aspect-[4/3] h-full w-full object-cover transition-transform duration-700 hover:scale-105"
							/>
						</div>
					</div>
					<div class={index % 2 === 1 ? 'lg:order-1' : ''}>
						<p class="font-heading text-sm font-semibold text-[#194139]">
							Experience {String(index + 1).padStart(2, '0')}
						</p>
						<h3 class="mt-2 font-heading text-2xl font-bold tracking-tight sm:text-3xl">
							{experience.title}
						</h3>
						<hr class="my-5 w-16 border-2 border-[#194139]" />
						<p class="leading-7 text-muted-foreground">{experience.content}</p>
						<Button
							href="#contact"
							onclick={handleAnchorClick}
							variant="link"
							data-sveltekit-noscroll
							class="mt-4 gap-2 px-0 text-base leading-7"
							>Enquire about this experience <ArrowRight class="h-4 w-4" /></Button
						>
					</div>
				</div>
			{/each}
		</div>
	</div>
</section>

<div class="h-32 w-full bg-gradient-to-b from-secondary to-background" aria-hidden="true"></div>

<section class="mx-auto w-full bg-background px-4 pb-12 pt-4" id="contact">
	<div class=" mx-auto max-w-7xl">
		<div class="mx-auto lg:mx-0">
			<h2
				class="text-center font-heading text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl"
			>
				Design your bespoke experience
			</h2>
			<div class="mx-auto mt-6 flex max-w-3xl">
				<form
					onsubmit={handleSubmit}
					data-sveltekit-preload-data="off"
					class=" relative flex w-full flex-col items-center justify-center gap-10 px-4 pb-12 pt-6"
				>
					<p class="max-w-lg text-center">
						Tell us what you have in mind and our team will craft a tailored, one-of-a-kind
						Tasmanian experience just for you.
					</p>
					<div class=" flex flex-wrap items-center justify-center">
						<Button variant="link" href="tel:0409404337" target="_blank" on:click={trackPhoneClick}
							>0409 404 337</Button
						> • <Button variant="link" href="mailto:info@tassietours.com" target="_blank"
							>info@tassietours.com</Button
						> •
						<Button variant="link" href="mailto:info@tassietours.com" target="_blank"
							>7am to 7pm</Button
						>
					</div>
					<div class="flex w-full flex-col gap-1.5">
						<Label for="name">Name</Label>
						<Input required type="text" id="name" placeholder="Name" bind:value={name} />
					</div>
					<div class="flex w-full gap-4">
						<div class="flex w-1/2 flex-col gap-1.5">
							<Label for="email">Email</Label>
							<Input
								required
								type="email"
								id="email"
								placeholder="email@domain.com"
								bind:value={email}
							/>
						</div>
						<div class="flex w-1/2 flex-col gap-1.5">
							<Label for="phone">Phone</Label>
							<Input
								required
								type="phone"
								id="phone"
								placeholder="Phone number"
								bind:value={phone}
							/>
						</div>
					</div>

					<div class="flex w-full flex-col gap-1.5">
						<Label for="preferredContact">Preferred contact method</Label>
						<Select.Root type="single" bind:value={preferredContact}>
							<Select.Trigger class="">
								{triggerContent}
							</Select.Trigger>
							<Select.Content>
								<Select.Group>
									{#each contactMethods as method}
										<Select.Item value={method.value} label={method.label}
											>{method.label}</Select.Item
										>
									{/each}
								</Select.Group>
							</Select.Content>
						</Select.Root>
					</div>
					<div class="flex w-full flex-col gap-1.5">
						<Label for="message-2">Message</Label>
						<Textarea
							required
							placeholder="Tell us about your ideal Tasmanian experience."
							id="message-2"
							bind:value={message}
						/>
						<p class="text-sm text-muted-foreground">
							Share your interests, occasion, group size or any special requests.
						</p>
					</div>
					<div class="relative mt-6 flex flex h-32 w-full items-center justify-center">
						{#if !submitted}
							<div
								class="absolute inset-0 flex justify-center"
								in:fly={{ x: -50, duration: 300 }}
								out:fly={{ x: -50, duration: 300 }}
							>
								{#if !loading}
									<Button
										type="submit"
										class="z-50 min-w-32 overflow-hidden rounded-full bg-[#194139] p-3 text-center font-semibold text-background transition-all hover:bg-[#194139]/80"
									>
										Submit
									</Button>
								{:else}
									<Button
										variant="outline"
										disabled
										class="z-50 flex min-w-32 overflow-hidden rounded-full bg-[#194139] p-3 text-center font-semibold text-background transition-all"
									>
										<Loader2 class="mr-2 h-4 w-4 animate-spin" />
									</Button>
								{/if}
							</div>
						{:else}
							<div
								class="absolute inset-0 mx-auto flex w-[500px] items-center justify-center"
								in:fly={{ x: 50, duration: 300, delay: 300 }}
								out:fly={{ x: -50, duration: 300 }}
							>
								<Alert.Root onclick={() => (submitted = false)} class="text-left hover:bg-accent">
									<CheckCircle class="h-4 w-4" />
									<Alert.Title>Your submission has been received!</Alert.Title>
									<Alert.Description class="text-wrap">
										Keep an eye out. A member of our team will get back to you within 3 business
										days.
									</Alert.Description>
								</Alert.Root>
							</div>
						{/if}
					</div>
				</form>
			</div>
		</div>
	</div>
</section>
